import { useEffect, useState } from 'react';
import useRedirectUrl from '~/client/components/Auth/hooks/useRedirectUrl';
import useFunModeToggle from '~/client/components/Game/GameView/GameViewDesktop/GameBlock/GameFooter/FunModeToggle/useFunModeToggle';
import updateAndOpenChat from '~/client/features/Comms/utils/updateAndOpenChat';
import useProto from '~/client/features/Proto/hooks/useProto';
import updateAndOpenChatProto from '~/client/features/Proto/utils/updateAndOpenChatProto';
import useFeature from '~/client/hooks/feature/useFeature';
import useForm from '~/client/hooks/form/useForm';
import useRouter from '~/client/hooks/router/useRouter';
import useAccountPopoverStore from '~/client/store/useAccountPopoverStore';
import useAppContext from '~/shared/context/AppContext/useAppContext';
import sendFormErrorGtm from '~/shared/gtm/sendFormErrorGtm';
import deserializeErrors from '~/shared/utils/errors/deserializeErrors';
import nativeReplace from '~/shared/utils/router/nativeReplace';
import type FormValues from '../../FormValues';
import type { RegisterMutation } from '../graphql/RegisterMutation.generated';

type Props = {
  isPopover?: boolean;
  onSuccess?: () => void;
};
const useHandlePayloadResponse = ({ isPopover, onSuccess }: Props) => {
  const {
    captchaConfig: { isRegistrationCaptchaRequired },
  } = useAppContext();
  const [hasCaptchaRequiredError, setHasCaptchaRequiredError] = useState(
    isRegistrationCaptchaRequired,
  );
  const [loading, setLoading] = useState(false);
  const { isProtoEnabled } = useProto();
  const redirectUrl = useRedirectUrl();
  const { push, asPath, reload } = useRouter();
  const { dismissPopover } = useAccountPopoverStore();
  const isFirstDepositBannerEnabled = useFeature('isFirstDepositBannerEnabled');
  const { handleChange } = useFunModeToggle();
  const [registerSuccessful, setRegisterSuccessful] = useState(false);
  const { setError } = useForm<FormValues>({});

  useEffect(() => {
    if (registerSuccessful) {
      if (isPopover) {
        dismissPopover(true);
      }
    }
  }, [registerSuccessful, dismissPopover, isPopover]);

  const handlePayloadResponse = (
    payload: RegisterMutation['userManagementRegisterAsUser'],
    setErrorFromPayload: (err: GenericErrorType[]) => void,
  ) => {
    if (payload?.errors?.length) {
      setLoading(false);
      setErrorFromPayload(payload.errors);
      const deserializedErrors = deserializeErrors(
        payload.errors,
      ) as DeserializeError;
      if (deserializedErrors?.captcha) {
        setHasCaptchaRequiredError(true);
      }
      if (deserializedErrors.dateOfBirth) {
        const [message] = deserializedErrors.dateOfBirth;
        setError('dateOfBirth.day', { message: `errors.${message}` });
      }
      return sendFormErrorGtm({
        event: 'registrationFail',
        errors: payload?.errors as GenericErrorType[],
      });
    }
    const userData = payload?.userManagement;
    if (isProtoEnabled) {
      updateAndOpenChatProto();
    } else {
      updateAndOpenChat({
        opts: {
          customer: {
            externalId: userData?.currentProfile?._id,
            name: userData?.currentUser?.username,
            email: userData?.currentUser?.email,
          },
        },
      });
    }
    setRegisterSuccessful(true);
    onSuccess?.();
    if (isPopover) {
      handleChange(false);
      return push(`${asPath}?c=home`).then(() => reload());
    }
    if (isFirstDepositBannerEnabled) {
      return push('/?c=home');
    }
    if (asPath.includes('/loyalty')) {
      return reload();
    }
    return nativeReplace(redirectUrl);
  };
  return {
    loading,
    hasCaptchaRequiredError,
    handlePayloadResponse,
  };
};

export default useHandlePayloadResponse;
